import React from "react";
import { 
  LocalHospital,
  Business,
  Science,
  Event,
  Videocam,
  Favorite,
  Timeline,
  Security
} from "@mui/icons-material";

interface LoginThemeWrapperProps {
  children: React.ReactNode;
  className?: string;
}

export default function LoginThemeWrapper({ children, className = "" }: LoginThemeWrapperProps) {
  return (
    <div className={`w-screen min-h-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-200 ${className}`}>
      {/* Animated Background Elements */}
      {/* <div className="absolute inset-0 overflow-hidden"> */}
        {/* Floating Orbs */}
        {/* <div className="absolute top-20 left-20 w-72 h-72 bg-blue-400/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-indigo-400/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-cyan-400/30 rounded-full blur-3xl animate-pulse delay-2000"></div>
         */}
  
      {/* </div> */}

      <div className="grid grid-cols-1 lg:grid-cols-2 w-full h-full">
        {/* Left Column - Premium Healthcare Branding */}
        <div className="hidden lg:block relative">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 backdrop-blur-sm"></div>
          <div className="relative z-10 flex flex-col justify-center p-16 text-white h-full">
            {/* Hero Section */}
            <div className="max-w-2xl">
              {/* Headline with Gradient Text */}
              <h1 className="text-6xl font-bold mb-8 leading-tight bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
                Transform Healthcare
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                  with Technology
                </span>
              </h1>
              
              {/* Description */}
              <p className="text-xl text-blue-100 mb-16 leading-relaxed opacity-90">
                Experience the future of healthcare management with our comprehensive platform. 
                Streamline operations, enhance patient care, and grow your practice with cutting-edge solutions.
              </p>
              
              {/* Features Grid with Hover Effects */}
              <div className="grid grid-cols-2 gap-8">
                <div className="group p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Business className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-blue-100 font-semibold text-lg">Smart OPD Management</span>
                  </div>
                </div>
                
                <div className="group p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Science className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-blue-100 font-semibold text-lg">Lab Integration</span>
                  </div>
                </div>
                
                <div className="group p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Event className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-blue-100 font-semibold text-lg">Appointment Scheduling</span>
                  </div>
                </div>
                
                <div className="group p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Videocam className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-blue-100 font-semibold text-lg">Teleconsultation</span>
                  </div>
                </div>
                
                <div className="group p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Favorite className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-blue-100 font-semibold text-lg">Patient Care</span>
                  </div>
                </div>
                
                <div className="group p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Timeline className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-blue-100 font-semibold text-lg">Analytics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Login Form */}
        <div className="flex items-center justify-center md:p-8 p-6">
          <div className="w-full max-w-md">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
